// Bulk student import from CSV (brief §8). Parse + per-row validate (12-digit
// LRN kept as a STRING so leading zeros survive), partial success (import the
// good rows, report the bad ones), and a non-blocking warning for LRNs that
// already exist in any class. The LRN is the only student identifier.
import Papa from 'papaparse';
import { repos } from './store.js';
import { navigate } from './router.js';
import { esc, toast } from './dom.js';
import { shareOrDownload } from '../export/csv.js';

const TEMPLATE_CSV = 'LRN,Name,Sex\n136800120001,Juan Dela Cruz,M\n136800120002,Maria Santos,F\n';

export interface ImportRow {
  lrn: string;
  name: string;
  sex: 'M' | 'F' | '';
}
export interface RowError {
  line: number;
  message: string;
}
export interface ParseResult {
  rows: ImportRow[];
  errors: RowError[];
  headerError?: string;
}

/** Pure parse + validation (no DB) — unit-testable. */
export function parseStudentCsv(text: string): ParseResult {
  const out: ParseResult = { rows: [], errors: [] };
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h) => h.trim().toLowerCase(),
  });
  const fields = parsed.meta.fields ?? [];
  if (!fields.includes('lrn') || !fields.includes('name')) {
    out.headerError = 'CSV needs a header row with at least LRN and Name columns (see the template).';
    return out;
  }
  parsed.data.forEach((r, i) => {
    const line = i + 2; // header is line 1
    const lrn = String(r.lrn ?? '').trim();
    const name = String(r.name ?? '').trim();
    const sexRaw = String(r.sex ?? '').trim().toUpperCase();
    if (!lrn && !name && !sexRaw) return; // skip blank line
    if (!/^\d{12}$/.test(lrn)) return void out.errors.push({ line, message: `LRN must be exactly 12 digits ("${lrn || 'blank'}")` });
    if (!name) return void out.errors.push({ line, message: 'Name is required' });
    if (sexRaw && sexRaw !== 'M' && sexRaw !== 'F') return void out.errors.push({ line, message: `Sex must be M or F ("${sexRaw}")` });
    out.rows.push({ lrn, name, sex: sexRaw === 'M' || sexRaw === 'F' ? sexRaw : '' });
  });
  return out;
}

/** Open the import dialog. classId preset when launched from a class roster. */
export async function importStudentsDialog(presetClassId?: string): Promise<void> {
  const classes = await repos.listClasses();
  if (!classes.length) return toast('Create a class first', 'err');

  const dlg = document.createElement('dialog');
  dlg.className = 'dlg';
  const classField = presetClassId
    ? ''
    : `<div class="field"><label for="imp_cls">Class</label><select class="input" id="imp_cls">${classes
        .map((c) => `<option value="${esc(c.id)}">${esc(c.name)}</option>`)
        .join('')}</select></div>`;
  dlg.innerHTML = `<form method="dialog"><h2 class="dlg-title">Import students (CSV)</h2>
    ${classField}
    <p class="hint">Columns: <strong>LRN, Name, Sex</strong>. LRN must be exactly 12 digits — it is how a scanned sheet is matched to the student.</p>
    <button type="button" class="btn btn-ghost btn-block" data-template>Download template</button>
    <div class="field" style="margin-top:12px"><label for="imp_file">CSV file</label>
      <input class="input" type="file" id="imp_file" accept=".csv,text/csv"></div>
    <div id="imp_summary"></div>
    <div class="dlg-actions"><button type="button" class="btn btn-ghost" data-cancel>Cancel</button>
      <button type="button" class="btn btn-primary" data-import disabled>Import</button></div></form>`;
  document.body.appendChild(dlg);
  const close = () => {
    dlg.close();
    dlg.remove();
  };
  const fileEl = dlg.querySelector<HTMLInputElement>('#imp_file')!;
  const importBtn = dlg.querySelector<HTMLButtonElement>('[data-import]')!;
  fileEl.addEventListener('change', () => (importBtn.disabled = !fileEl.files?.length));

  dlg.querySelector('[data-template]')!.addEventListener('click', () =>
    shareOrDownload('student_import_template.csv', TEMPLATE_CSV),
  );
  dlg.querySelector('[data-cancel]')!.addEventListener('click', close);
  dlg.addEventListener('cancel', close);

  importBtn.addEventListener('click', async () => {
    const file = fileEl.files?.[0];
    if (!file) return;
    const classId = presetClassId ?? dlg.querySelector<HTMLSelectElement>('#imp_cls')!.value;
    const text = await file.text();
    const parsed = parseStudentCsv(text);
    const summary = dlg.querySelector<HTMLDivElement>('#imp_summary')!;

    if (parsed.headerError) {
      summary.innerHTML = `<p class="field-err">${esc(parsed.headerError)}</p>`;
      return;
    }

    // The LRN is the unique student key, so a row whose LRN already exists
    // (in ANY class) or repeats within the file is skipped — warned, not created
    // as a conflicting duplicate. Fresh LRNs import.
    const [existing, allClasses] = await Promise.all([repos.allStudents(), repos.listClasses()]);
    const clsName = new Map(allClasses.map((c) => [c.id, c.name]));
    const lrnWhere = new Map<string, string>();
    for (const s of existing) if (!lrnWhere.has(s.lrn)) lrnWhere.set(s.lrn, clsName.get(s.classId) ?? '?');
    const seen = new Set<string>();
    const fresh: typeof parsed.rows = [];
    const dups: Array<ImportRow & { where: string }> = [];
    for (const r of parsed.rows) {
      if (lrnWhere.has(r.lrn)) dups.push({ ...r, where: lrnWhere.get(r.lrn)! });
      else if (seen.has(r.lrn)) dups.push({ ...r, where: 'repeated in file' });
      else {
        seen.add(r.lrn);
        fresh.push(r);
      }
    }

    let added = 0;
    try {
      if (fresh.length) added = (await repos.addStudents(classId, fresh)).length;
    } catch (e: any) {
      summary.innerHTML = `<p class="field-err">${esc(e?.message ?? 'Import failed')}</p>`;
      return;
    }

    // Replace the form body with a result summary.
    const errList = parsed.errors.length
      ? `<p class="section-label">Invalid — skipped ${parsed.errors.length} row(s)</p><ul class="import-log">${parsed.errors
          .map((e) => `<li class="err">Line ${e.line}: ${esc(e.message)}</li>`)
          .join('')}</ul>`
      : '';
    const dupList = dups.length
      ? `<p class="section-label">${dups.length} duplicate LRN(s) — skipped</p><ul class="import-log">${dups
          .map((d) => `<li class="warn">${esc(d.name)} (LRN ${esc(d.lrn)}) already in ${esc(d.where)}</li>`)
          .join('')}</ul>`
      : '';
    dlg.querySelector('form')!.innerHTML = `<h2 class="dlg-title">Import complete</h2>
      <p><strong>${added}</strong> student(s) added${parsed.errors.length ? ` · ${parsed.errors.length} invalid` : ''}${dups.length ? ` · ${dups.length} duplicate(s)` : ''}.</p>
      ${dupList}${errList}
      <div class="dlg-actions"><button type="button" class="btn btn-primary btn-block" data-done>Done</button></div>`;
    dlg.querySelector('[data-done]')!.addEventListener('click', () => {
      close();
      if (added) navigate(location.hash || '#/students'); // refresh the roster
    });
  });

  dlg.showModal();
}
